import React, { useContext } from 'react';
import { StorageProviderKey } from '../../models/storageProviderKeys';
import { DropboxContext } from '../../services/dropbox/dropboxService';
import { GoogleApiContext, GoogleScope } from '../../services/gapi/gapiService';
import { OneDriveContext } from '../../services/oneDrive/oneDriveService';
import { localStorageProviderEnabled } from '../../services/storage/localStorageProvider';
import { Context } from '../../services/_globalContext/context';
import AuthorizeButton from '../common/authorizeButton/authorizeButton';
import { googleSessionInfo } from '../common/infoBubble/infoTexts';
import ServiceRow from './serviceRow';
import s from './settingsComponent.module.scss';

interface Props {
  setup?: boolean;
}

/**
 * The three accounts that can hold the library, one row each. Shared by the
 * setup screen (Advanced) and the Storage group on the settings page.
 *
 * A signed-in row is its logo and its buttons, and nothing else: a Logout
 * button is what says it is signed in, so no row says "connected". Google's
 * sign-in is Google's own artwork, as their branding rules ask, and carries
 * the logo itself - so a signed-out Google row shows the button alone rather
 * than the mark twice.
 */
export default function ProviderList({ setup }: Props) {
  const ctx = useContext(Context);
  const gapiCtx = useContext(GoogleApiContext);
  const oneDriveCtx = useContext(OneDriveContext);
  const dbxCtx = useContext(DropboxContext);

  // Google Identity Services issues access tokens and nothing else - there is
  // no profile behind this any more, so no email to show without spending an
  // extra scope and a userinfo request on it.
  const googleAccount = gapiCtx.getAccount();
  const googleUser = gapiCtx.googleUser;
  const oneDriveAccount = oneDriveCtx.isAuthenticated && oneDriveCtx.getAccount();
  const googleConnected = gapiCtx.authorizedScopes.indexOf(GoogleScope.AppData) !== -1;

  /** Nothing to offer when this provider is already the one in use. */
  function setAsProvider(key: StorageProviderKey) {
    if (ctx.storageProvider.current?.name === key) {
      return null;
    }

    return (
      <button className="button" onClick={() => ctx.configureStorageProvider(key)}>
        Set as storage provider
      </button>
    );
  }

  return (
    <ul className={s.serviceList}>
      <ServiceRow
        // Signed out, Google's own button is the logo.
        brand={googleConnected ? 'googleDrive' : undefined}
        info={googleSessionInfo}
        status={
          googleConnected
            ? googleAccount
              ? // Whose account it is, like every other row. Comes from Drive
                // rather than the token - see googleUser in gapiService.
                googleUser?.email || googleUser?.name
              : // Something wrong with it: the token has lapsed and needs the
                // button pressed again.
                'Authorisation expired'
            : undefined
        }
      >
        {googleConnected ? (
          <>
            <button className="button" onClick={gapiCtx.logout}>
              Logout
            </button>
            {setAsProvider(StorageProviderKey.GoogleDrive)}
          </>
        ) : (
          <AuthorizeButton
            google
            busy={gapiCtx.isAuthorizing}
            onClick={
              setup
                ? () =>
                    gapiCtx
                      .authorize({
                        scope: GoogleScope.AppData,
                        allowRedirect: true,
                        // Carries the same instruction as the .then below, for the
                        // redirect path where the .then never runs.
                        intent: { configureProvider: true },
                      })
                      .then(
                        () => ctx.configureStorageProvider(StorageProviderKey.GoogleDrive),
                        ctx.callSnackbar,
                      )
                : () =>
                    gapiCtx
                      .authorize({
                        scope: GoogleScope.AppData,
                        allowRedirect: true,
                      })
                      .catch(ctx.callSnackbar)
            }
          />
        )}
      </ServiceRow>

      <ServiceRow
        brand="oneDrive"
        // Which account it is, which is worth saying where there can be more
        // than one - unlike "connected", which the button says. MSAL's
        // username is the sign-in address; name is the display name, and is
        // not always set.
        status={oneDriveAccount ? oneDriveAccount.username || oneDriveAccount.name : undefined}
      >
        {oneDriveAccount ? (
          <>
            <button className="button" onClick={oneDriveCtx.logout}>
              Logout
            </button>
            {setAsProvider(StorageProviderKey.OneDrive)}
          </>
        ) : (
          <AuthorizeButton
            busy={oneDriveCtx.isAuthorizing}
            onClick={
              setup
                ? () =>
                    oneDriveCtx
                      .authorize({
                        allowRedirect: true,
                        // Carries the same instruction as the .then below, for the
                        // redirect path where the .then never runs.
                        intent: { configureProvider: true },
                      })
                      .then(
                        () => ctx.configureStorageProvider(StorageProviderKey.OneDrive),
                        ctx.callSnackbar,
                      )
                : () => oneDriveCtx.authorize({ allowRedirect: true }).catch(ctx.callSnackbar)
            }
          >
            Login
          </AuthorizeButton>
        )}
      </ServiceRow>

      <ServiceRow brand="dropbox" status={dbxCtx.account ? dbxCtx.account.email : undefined}>
        {dbxCtx.account ? (
          <>
            <button className="button" onClick={dbxCtx.logout}>
              Logout
            </button>
            {setAsProvider(StorageProviderKey.Dropbox)}
          </>
        ) : (
          <AuthorizeButton
            busy={dbxCtx.isAuthorizing}
            onClick={
              setup
                ? () =>
                    dbxCtx
                      .authorize({
                        allowRedirect: true,
                        // Carries the same instruction as the .then below, for the
                        // redirect path where the .then never runs.
                        intent: { configureProvider: true },
                      })
                      .then(
                        () => ctx.configureStorageProvider(StorageProviderKey.Dropbox),
                        ctx.callSnackbar,
                      )
                : () => dbxCtx.authorize({ allowRedirect: true }).catch(ctx.callSnackbar)
            }
          >
            Login
          </AuthorizeButton>
        )}
      </ServiceRow>

      {localStorageProviderEnabled && setup && (
        // Development builds only, and gone entirely from a deployed one.
        // Mostly here for the browser tests, which cannot sign in to anything
        // - but it is the quickest way to get a throwaway library by hand too.
        //
        // Setup screen only: on the settings page this same choice lives
        // under Advanced, where it can say whether it is already in use.
        <ServiceRow>
          <button
            className="button secondary"
            onClick={() => ctx.configureStorageProvider(StorageProviderKey.Local)}
          >
            Use this browser (dev)
          </button>
        </ServiceRow>
      )}
    </ul>
  );
}
